import { COLOR } from "../../common/Color"
import { log } from "../../common/Logging"
import { DrTimeImage } from "../activities/dr-time/DrTimeAssets"
import { colorNum } from "../utils/ColorUtils"
import { PhaserText } from "../utils/PhaserText"
import { Scale } from "../utils/Scale"
import { Sprites } from "../utils/Sprites"
import { Buttons } from "./Buttons"
import { Rectangle } from "./Shape"


interface CheckboxInfo {
    checkbox: Rectangle
    check: Phaser.Sprite
    isCorrect: boolean
    textSprite?: Phaser.Text
    onClick: () => void
}

const checkboxWidth = () => Scale.x(0.02)

export class Checkbox {
    info: CheckboxInfo
    private isChecked: boolean

    constructor(
        private game: Phaser.Game,
        private group: Phaser.Group,
        onSelect: () => void,
        x: number,
        y: number,
        enabled = false,
        option: CheckboxOption,
        fontPx = 18
    ) {
        const checkboxY = y + fontPx * 0.85
        const checkbox = new Rectangle(game, x, checkboxY, checkboxWidth(), checkboxWidth(), {
            lineWidth: 1,
            fillColor: colorNum.white,
            group
        })
        const check = Sprites.add(this.game, DrTimeImage.CHECKMARK, { scale: .03, group })
        check.centerX = checkbox.graphics.centerX
        check.centerY = checkbox.graphics.centerY
        const onClick = () => {
            if (option.audioKey) {
                game.sound.stopAll()
                game.add.audio(option.audioKey).play()
            }
            this.isChecked = true
            onSelect()
        }
        const textSprite: Phaser.Text = PhaserText.create(game, option.text, {
            x: x + checkbox.invisibleSprite.width + Scale.x(0.01), y: y + Scale.y(.013), group, fontPx, color: COLOR.white
        })
        this.info = {checkbox, check, isCorrect: option.isCorrect, onClick, textSprite}
        this.selected = false
        if (enabled) {
            this.enable()
        }
    }

    get selected(): boolean {
        return this.isChecked
    }

    set selected(s: boolean) {
        this.isChecked = s
        this.info.check.alpha = s ? 1 : 0
    }

    enable = () => {
        const {checkbox, textSprite, onClick} = this.info
        Buttons.makeSpriteButton(checkbox.invisibleSprite, onClick)
        if (textSprite) {
            Buttons.makeSpriteButton(textSprite, onClick)
        }
    }

    disable = () => {
        const {checkbox, textSprite} = this.info
        Buttons.undoSpriteButton(checkbox.invisibleSprite)
        if (textSprite) {
            Buttons.undoSpriteButton(textSprite)
        }
    }

    setVisible = (visible: boolean) => {
        const {checkbox, check, textSprite} = this.info
        checkbox.graphics.visible = visible

        if (this.selected && !visible) {
            // If button has been selected, and we want to hide the button:
            check.alpha = 0
        } else if (this.selected && visible) {
            // If button has been selected, we want to show it, and the select is currently invisible:
            check.alpha = 1
        }

        if (textSprite) {
            textSprite.visible = false
        }

        if (!visible) {
            this.disable()
        }
    }

    destroy = () => {
        const {checkbox, check, textSprite} = this.info
        checkbox.destroy()
        check.destroy()
        if (textSprite) {
            textSprite.destroy()
        }
    }
}

export interface CheckboxOption {
    text: string
    isCorrect: boolean
    audioKey?: string
}

export class CheckboxGroup {
    currentlySelected?: string[] = []
    currentlySelectedIndexes?: number[] = []
    group: Phaser.Group
    checkboxes: Array<Checkbox>

    constructor(
        game: Phaser.Game,
        x: number,
        y: number,
        options: Array<CheckboxOption>,
        selectionPerformed: (isCorrect: boolean) => void,
        allCorrectSelected: () => void,
        enabled = true,
        horizontal = false,
        checkboxWrapLength: number,
        checkboxWrapDistance: number,
        fontPx = 18,
        group?: Phaser.Group
    ) {
        let currentX = x + (checkboxWidth() / 2)
        let currentY = y
        this.group = group ? group : game.add.group()

        function numCorrectOptions(): number {
            const correctOptions = options.filter(option => {
                return option.isCorrect
            })
            return correctOptions.length
        }

        this.checkboxes = options.map((option, i) => {
            const checkbox = new Checkbox(game, this.group, () => {
                if (option.isCorrect && (!this.currentlySelectedIndexes || !this.currentlySelectedIndexes.includes(i))) {
                    this.currentlySelected.push(option.text)
                    this.currentlySelectedIndexes.push(i)
                    checkbox.selected = true
                    checkbox.disable()
                }
                if (this.currentlySelectedIndexes.length == numCorrectOptions()) {
                    this.checkboxes.forEach(checkbox => checkbox.disable())
                    allCorrectSelected()
                }
                log.info(this.currentlySelectedIndexes)
                log.info(this.currentlySelected)
                selectionPerformed(option.isCorrect)
            }, currentX, currentY, enabled, option, fontPx)

            if (horizontal) {
                currentX += checkbox.info.textSprite.width + Scale.x(0.08)
                if (i !== 0 && (i + 1) % checkboxWrapLength == 0) {
                    currentY += checkboxWrapDistance
                    currentX = x + (checkboxWidth() / 2)
                }
            } else {
                currentY += checkbox.info.textSprite.height + Scale.y(0.01)
                if (i !== 0 && (i + 1) % checkboxWrapLength == 0) {
                    currentX += checkboxWrapDistance
                    currentY = y
                }
            }

            return checkbox
        })
    }

    enable = () => this.checkboxes.forEach(button => button.enable())

    disable = () => this.checkboxes.forEach(button => button.disable())

    destroy = () => this.checkboxes.forEach(button => button.destroy())
}
